import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import styles from "./css/transaction.module.css";

/**
 * Helper to generate page ranges with ellipsis (...) matching the reference design.
 */
const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const delta = 1;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta) ||
      (currentPage <= 4 && i <= 5) ||
      (currentPage >= totalPages - 3 && i >= totalPages - 4)
    ) {
      range.push(i);
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

/**
 * Reusable capsule pill-style table pagination component with Framer Motion animations.
 */
const TablePaginationFooter = ({
  currentPage = 1,
  totalCount = 0,
  limit = 10,
  currentCount = 0,
  onPageChange,
  onLimitChange,
  viewAllLink,
  viewAllText = "View All Transactions",
  showPagination = true,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const totalPages = Math.ceil(totalCount / limit) || 1;
  const startItem = currentCount > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endItem = Math.min(currentPage * limit, totalCount);

  const paginationItems = getPaginationItems(currentPage, totalPages);

  return (
    <div className={styles.tableCardFooter}>
      {/* Pagination Info */}
      <div className={styles.paginationInfo}>
        {viewAllLink ? (
          <>
            Showing <span>{currentCount}</span> of <span>{totalCount}</span> recent entries
          </>
        ) : (
          <>
            Showing <span>{startItem}</span> to <span>{endItem}</span> of <span>{totalCount}</span> entries
          </>
        )}
      </div>

      {/* View All Button OR Animated Pill Pagination Bar */}
      {viewAllLink ? (
        <motion.div
          whileHover={shouldReduceMotion ? {} : { scale: 1.04, y: -1 }}
          whileTap={shouldReduceMotion ? {} : { scale: 0.96 }}
        >
          <Link to={viewAllLink} className={styles.btnFooterViewAll}>
            {viewAllText} <i className="fas fa-arrow-right ms-2"></i>
          </Link>
        </motion.div>
      ) : (
        showPagination && (
          <motion.div
            className={styles.paginationPillBar}
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 380, damping: 25 }}
          >
            {/* Previous Page Arrow */}
            <motion.button
              type="button"
              className={styles.pageArrowBtn}
              onClick={() => onPageChange && onPageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              title="Previous Page"
              whileHover={shouldReduceMotion || currentPage === 1 ? {} : { scale: 1.18, x: -2 }}
              whileTap={shouldReduceMotion || currentPage === 1 ? {} : { scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <i className="fas fa-chevron-left"></i>
            </motion.button>

            {/* Page Number Circle Buttons & Animated Active Highlight */}
            {paginationItems.map((item, idx) => {
              if (item === "...") {
                return (
                  <span key={`dots-${idx}`} className={styles.paginationDots}>
                    ...
                  </span>
                );
              }
              const isActive = currentPage === item;
              return (
                <motion.button
                  key={item}
                  type="button"
                  onClick={() => onPageChange && onPageChange(item)}
                  className={`${styles.pageNumberItem} ${isActive ? styles.activePageText : ""}`}
                  whileHover={shouldReduceMotion ? {} : { scale: 1.15 }}
                  whileTap={shouldReduceMotion ? {} : { scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 22 }}
                >
                  {isActive && (
                    <motion.span
                      layoutId="activePageCircleBg"
                      className={styles.activePageCircleBg}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                  <span className={styles.pageNumberLabel}>{item}</span>
                </motion.button>
              );
            })}

            {/* Next Page Arrow */}
            <motion.button
              type="button"
              className={styles.pageArrowBtn}
              onClick={() => onPageChange && onPageChange(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              title="Next Page"
              whileHover={shouldReduceMotion || currentPage === totalPages ? {} : { scale: 1.18, x: 2 }}
              whileTap={shouldReduceMotion || currentPage === totalPages ? {} : { scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <i className="fas fa-chevron-right"></i>
            </motion.button>

            {/* Page Size Selector (10 / page) */}
            {typeof onLimitChange === "function" && (
              <motion.div
                className={styles.limitSelectWrapper}
                whileHover={shouldReduceMotion ? {} : { scale: 1.03 }}
              >
                <select
                  value={limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  className={styles.limitSelect}
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
                <i className={`fas fa-chevron-down ${styles.limitSelectCaret}`}></i>
              </motion.div>
            )}
          </motion.div>
        )
      )}
    </div>
  );
};

export default TablePaginationFooter;
